import { Injectable, inject } from '@angular/core';
import axios from 'axios';
import { AnalyticsService, trackTimedApiCall } from '../../integrations/analytics';
import { AXIOS_INSTANCE } from '../api-client.tokens';
import type {
  CancelShipmentRequest,
  CancelShipmentSuccessResponse,
} from './models/cancel-shipment.model';
import type { CreateShipmentRequest, CreateShipmentResponse } from './models/create-shipment.model';

/**
 * Client for Shipment Manager create, payment-continuation, and cancel requests.
 */
@Injectable({ providedIn: 'root' })
export class ShipmentApiService {
  private readonly http = inject(AXIOS_INSTANCE);
  private readonly analytics = inject(AnalyticsService);

  /**
   * Posts the complete attestation chain bound to the active wizard flow.
   * Tags the request with a client-generated correlation id so a failed call can be
   * matched to CloudWatch logs even though the UI only ever shows a generic deny message.
   */
  async create(flowId: string, request: CreateShipmentRequest): Promise<CreateShipmentResponse> {
    const correlationId = crypto.randomUUID();
    return trackTimedApiCall(this.analytics, 'createShipment', async () => {
      try {
        const response = await this.http.post<CreateShipmentResponse>(
          '/shipments/create',
          request,
          {
            headers: {
              'Content-Type': 'application/json',
              'x-flow-id': flowId,
              'x-correlation-id': correlationId,
            },
          },
        );
        return response.data;
      } catch (error) {
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;
        console.error('Shipment create request failed', { correlationId, flowId, status });
        throw error;
      }
    });
  }

  /**
   * Cancels a confirmed shipment within the OM-69 same-day window.
   * `flowId` is only required when sending `cancelToken` (email deep-link attestation).
   */
  async cancel(
    request: CancelShipmentRequest,
    options?: { flowId?: string },
  ): Promise<CancelShipmentSuccessResponse> {
    const correlationId = crypto.randomUUID();
    const flowId = options?.flowId?.trim();
    return trackTimedApiCall(this.analytics, 'cancelShipment', async () => {
      try {
        const response = await this.http.post<CancelShipmentSuccessResponse>(
          '/shipments/cancel',
          request,
          {
            headers: {
              'Content-Type': 'application/json',
              'x-correlation-id': correlationId,
              ...(flowId ? { 'x-flow-id': flowId } : {}),
            },
          },
        );
        return response.data;
      } catch (error) {
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;
        console.error('Shipment cancel request failed', { correlationId, flowId, status });
        throw error;
      }
    });
  }
}
